// =============================================================================
// SimSense Platform — Setup Detail Modal (shared component)
// =============================================================================
// Reusable modal that displays setup details for a session.
// Used on both SessionOverview and LapComparison pages.
// =============================================================================

import { useState } from "react";
import { useUnits } from "../hooks/useUnits";
import { useToast } from "./Toast";
import { buildSetupSections, renderSetupValue } from "./SetupDiffViewer";
import { getSessionCarFolderHint, installSessionSetupFromLocal, isDesktopSetupInstallAvailable } from "../utils/setupInstaller";

export { isDesktopSetupInstallAvailable };

interface SetupModalProps {
  setup: any;
  setupName?: string | null;
  rawSetupName?: string | null;
  sessionType?: string | null;
  carName?: string;
  sessionMetadata?: any;
  isDesktop: boolean;
  onClose: () => void;
}

export function SetupModal({
  setup,
  setupName,
  rawSetupName,
  sessionType,
  carName,
  sessionMetadata,
  isDesktop,
  onClose,
}: SetupModalProps) {
  const units = useUnits();
  const toast = useToast();
  const [isInstalling, setIsInstalling] = useState(false);
  if (!setup) return null;

  const sections = buildSetupSections(setup);

  const isAttachKey = (flatKey: string) => {
    const lk = flatKey.toLowerCase();
    return lk.includes("attach") || lk.includes("locked") || lk.includes("enabled");
  };
  const isAttachVal = (flatKey: string, val: any) =>
    isAttachKey(flatKey) && (val === 0 || val === 1 || val === "0" || val === "1");

  const renderAttach = (val: any) => {
    const checked = Number(val) === 1;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: "18px", height: "18px",
        border: `2px solid ${checked ? "var(--good)" : "var(--text3)"}`,
        borderRadius: "4px",
        background: checked ? "var(--good)" : "transparent",
        color: "#fff", fontSize: "12px", fontWeight: 700,
        lineHeight: 1,
      }}>
        {checked ? "\u2713" : ""}
      </span>
    );
  };

  const canInstall = isDesktop && Boolean(rawSetupName?.trim());

  const handleInstall = async () => {
    if (!canInstall || !rawSetupName) return;
    setIsInstalling(true);
    try {
      const result = await installSessionSetupFromLocal({
        carName,
        rawSetupName,
        carFolderHint: getSessionCarFolderHint(sessionMetadata),
      });
      const destinationCount = result.destinationDirs.length;
      toast.success(
        destinationCount > 1
          ? `Installed .sto into ${destinationCount} iRacing setup folders.`
          : "Installed .sto into your SimSense iRacing setup folder."
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to install the .sto file.");
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", width: "100%", maxWidth: "900px", maxHeight: "90vh", borderRadius: "12px", border: "1px solid var(--border)", padding: "24px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid var(--border)", paddingBottom: "16px" }}>
          <div>
            <h3 style={{ fontSize: "20px", marginBottom: "4px" }}>{sessionType || "Setup"}</h3>
            <div style={{ fontSize: "12px", color: "var(--text2)" }}>{setupName || carName || "Default Setup"}</div>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <button
              onClick={() => void handleInstall()}
              disabled={!canInstall || isInstalling}
              title={!isDesktop
                ? "This action is available in the desktop app."
                : !rawSetupName
                  ? "No .sto source name was available for this session."
                  : "Copy the matching .sto into your SimSense iRacing setup folder."}
              style={{
                padding: "8px 16px",
                background: canInstall && !isInstalling ? "var(--card)" : "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                color: canInstall && !isInstalling ? "var(--text)" : "var(--text3)",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: canInstall && !isInstalling ? "pointer" : "not-allowed"
              }}
            >
              {isInstalling ? "Installing..." : isDesktop ? "Install .sto" : "Install .sto (Desktop Only)"}
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "24px" }}>x</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", paddingRight: "10px", display: "flex", flexDirection: "column", gap: "20px" }}>
          {sections.map(section => (
            <div key={section.name}>
              <h4 style={{ fontSize: "12px", textTransform: "uppercase", color: "var(--accent)", marginBottom: "12px", borderBottom: "1px solid var(--border)", paddingBottom: "4px", letterSpacing: "0.5px" }}>
                {section.name}
              </h4>
              {section.pairs.map((pair, pi) => (
                <div key={pair.label || `pair-${pi}`} style={{ marginBottom: "12px" }}>
                  {pair.label && (
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", marginBottom: "6px" }}>{pair.label}</div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: "6px", padding: "10px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", marginBottom: "6px", textAlign: "center" }}>{pair.left.label}</div>
                      {pair.left.items.map(item => (
                        <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                          <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                          {isAttachVal(item.flatKey, item.value)
                            ? renderAttach(item.value)
                            : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: "6px", padding: "10px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", marginBottom: "6px", textAlign: "center" }}>{pair.right.label}</div>
                      {pair.right.items.map(item => (
                        <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                          <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                          {isAttachVal(item.flatKey, item.value)
                            ? renderAttach(item.value)
                            : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {section.shared.length > 0 && (() => {
                const isCentered = section.name.toLowerCase().includes("chassis") || section.name === "Rear Settings";
                return (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    ...(isCentered ? {
                      maxWidth: "50%",
                      margin: "12px auto 0",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "6px",
                      padding: "10px",
                    } : {}),
                  }}>
                    {section.shared.map(item => (
                      <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                        <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                        {isAttachVal(item.flatKey, item.value)
                          ? renderAttach(item.value)
                          : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
